import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { PadlockService } from "../padlock/padlock.service";

export const FORCE_PASSWORD_CHANGE_ROUTE = "/user/force-password-change";
export const FORCE_PASSWORD_CHANGE_COMPLETED_USER_KEY =
  "force-password-change-completed-user";

export function buildForcePasswordCompletionMarker(
  userId: string,
  decodedToken: any,
): string {
  const issuedAt = decodedToken?.iat || decodedToken?.exp || "";
  return `${userId}:${issuedAt}`;
}

@Injectable({ providedIn: "root" })
export class TemporaryPasswordGuardService {
  constructor(
    private router: Router,
    private padlockService: PadlockService,
    private logger: NGXLogger,
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    return this.enforceRoute(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    return this.canActivate(route, state);
  }

  private enforceRoute(url: string): boolean {
    const decoded = this.padlockService.getDecodedToken();
    const userId = decoded?.sub;

    if (!decoded || !userId) {
      return true;
    }

    const requiresPasswordChange = this.hasTemporaryPasswordFlag(decoded);
    const isForcePasswordRoute = url.startsWith(FORCE_PASSWORD_CHANGE_ROUTE);
    const completionMarker = buildForcePasswordCompletionMarker(
      userId,
      decoded,
    );

    if (!requiresPasswordChange) {
      sessionStorage.removeItem(FORCE_PASSWORD_CHANGE_COMPLETED_USER_KEY);
      if (isForcePasswordRoute) {
        this.router.navigateByUrl("/");
        return false;
      }
      return true;
    }

    if (this.hasCompletedForcePasswordChange(completionMarker)) {
      return true;
    }

    if (isForcePasswordRoute) {
      return true;
    }

    this.logger.debug(
      "TEMP PASSWORD GUARD: redirecting to force password route",
    );
    this.router.navigateByUrl(FORCE_PASSWORD_CHANGE_ROUTE);
    return false;
  }

  private hasCompletedForcePasswordChange(completionMarker: string): boolean {
    return (
      sessionStorage.getItem(FORCE_PASSWORD_CHANGE_COMPLETED_USER_KEY) ===
      completionMarker
    );
  }

  private hasTemporaryPasswordFlag(decodedToken: any): boolean {
    const candidates = [
      decodedToken?.temporary_password,
      decodedToken?.temp_password,
      decodedToken?.must_change_password,
      decodedToken?.force_password_change,
      decodedToken?.password_reset_required,
    ];

    return candidates.some((value) => value === true || value === "true");
  }
}
